import { useState, useEffect } from "react";
import { connect } from "react-redux";
import { NavLink, useLocation, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Icon from "react-icons-kit";
import { standby } from "react-icons-kit/iconic/standby";
import { home } from "react-icons-kit/feather/home";
import { users } from "react-icons-kit/feather/users";
import { creditCard } from "react-icons-kit/feather/creditCard";
import { smartphone } from "react-icons-kit/feather/smartphone";
import { fileText } from "react-icons-kit/feather/fileText";
import { grid } from "react-icons-kit/feather/grid";
import { settings } from "react-icons-kit/feather/settings";
import { chevronRight } from "react-icons-kit/feather/chevronRight";
import { chevronDown } from "react-icons-kit/feather/chevronDown";
import { userPlus } from "react-icons-kit/feather/userPlus";
import { userCheck } from "react-icons-kit/feather/userCheck";
import { send } from "react-icons-kit/feather/send";
import { inbox } from "react-icons-kit/feather/inbox";
import { search } from "react-icons-kit/feather/search";
import { rotateCcw } from "react-icons-kit/feather/rotateCcw";
import { shoppingCart } from "react-icons-kit/feather/shoppingCart";
import { archive } from "react-icons-kit/feather/archive";
import { cornerUpLeft } from "react-icons-kit/feather/cornerUpLeft";
import { list } from "react-icons-kit/feather/list";
import { clipboard } from "react-icons-kit/feather/clipboard";
import { truck } from "react-icons-kit/feather/truck";
import { dollarSign } from "react-icons-kit/feather/dollarSign";
import { database } from "react-icons-kit/feather/database";
import { shield } from "react-icons-kit/feather/shield";
import { lock } from "react-icons-kit/feather/lock";
import { userLogout } from "../redux/auth/action";
import { showModal } from "../redux/modal/actions";
import IntlMessages from "../utils/IntlMessages";
import { getUserMenus } from "../utils/UserDetails";

const Sidebar = ({ userLogout, showModal }) => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [menus, setMenus] = useState([]); 

  const location = useLocation();
  const history = useHistory();
  const { i18n } = useTranslation();

  // Icon mapping for parent menus
  const getMenuIcon = (menuId) => {
    const iconMap = {
      dashboard: home,           
      users: users,              
      voucher: creditCard,       
      sim: smartphone,           
      report: fileText,          
      tool: grid,                
      settings: settings,        
    };
    return iconMap[menuId] || fileText;
  };

  // Icon mapping for child menus - based on actual API IDs
  const getChildIcon = (childId) => {
    const childIconMap = {
      // Users submenu
      "users.users": userPlus,                     
      "users.office.users": userCheck,             
      
      // SIM submenu
      "sim.distribution": send,                    
      "sim.invoice": inbox,                         
      "sim.search": search,                         
      "sim.invoice.return": rotateCcw,              
      
      // Voucher submenu
      "voucher.card.invoice": shoppingCart,         
      "voucher.management": archive,               
      "voucher.search": search,                     
      "voucher.return.invoice": cornerUpLeft,       
      "voucher.invoice.list": list,                 
      "voucher.return.invoice.list": clipboard,     
      
      // Report submenu
      "report.delivery.note": truck,                
      "report.customer.invoice": dollarSign,        
      "report.total.stock": database,               
      
      // Tools submenu
      "tools.permissions": shield,                  
      
      // Settings submenu
      "tools.change.password": lock,                
    };
    return childIconMap[childId] || fileText;
  };

  useEffect(() => {
    const storedMenus = getUserMenus();
    setMenus(Array.isArray(storedMenus) ? storedMenus : []);
  }, []);

  /* 🔹 Keep parent menu open if child is active */
  useEffect(() => {
    if (!menus.length) return;

    const activeParents = {};

    menus.forEach((menu) => {
      if (menu.children?.length) {
        const hasActiveChild = menu.children.some(
          (child) => child.to === location.pathname
        );
        if (hasActiveChild) activeParents[menu.id] = true;
      }
    });

    setExpanded((prev) => ({
      ...prev,
      ...activeParents,
    }));
  }, [location.pathname, menus]);

  /* 🔹 Toggle menu */
  const toggleMenu = (id) => {
    setExpanded((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  /* 🔹 Handle logout with confirmation modal */
  const handleLogout = () => {
    showModal({
      title: "Confirm Logout",
      message: "Are you sure you want to logout?",
      yesLabel: "Logout",
      noLabel: "Stay",
      yesType: "api",
      onYes: () => userLogout(history, "/"),
      onNo: () => console.log("Stayed"),
    });
  };

  return (
    <>
      {/* Mobile navbar */}
      <nav className="navbar navbar-light bg-light d-md-none">
        <button className="btn btn-primary" onClick={() => setOpen(true)}>
          ☰ Menu
        </button>
      </nav>

      {/* Sidebar */}
      <div className={`fixed-sidebar ${open ? "show" : ""}`}>
        <div className="sidebar-header">
          <h5 className="sidebar-title">
            <Icon icon={fileText} size={20} className="me-2" />
            Invoice Management
          </h5>
          <button
            className="btn-close-sidebar d-md-none"
            onClick={() => setOpen(false)}
          >
            ✕
          </button>
        </div>

        {/* 🔹 Menu Rendering */}
        <div className="sidebar-menu">
          {menus.map((menu) => {
            const isParentActive =
              menu.children?.some((c) => c.to === location.pathname) ||
              location.pathname === menu.to;

            return (
              <div key={menu.id} className="menu-item-wrapper">
                {/* Parent menu */}
                {menu.children?.length ? (
                  <>
                    <div
                      className={`menu-parent ${
                        isParentActive ? "active" : ""
                      }`}
                      onClick={() => toggleMenu(menu.id)}
                    >
                      <div className="menu-parent-content">
                        <Icon icon={getMenuIcon(menu.id)} size={18} className="menu-icon" />
                        <span className="menu-text">
                          <IntlMessages id={menu.id} />
                        </span>
                      </div>
                      <Icon 
                        icon={expanded[menu.id] ? chevronDown : chevronRight} 
                        size={16} 
                        className="menu-arrow"
                      />
                    </div>

                    {/* Child menus */}
                    {expanded[menu.id] && (
                      <div className="menu-child">
                        {menu.children.map((child) => (
                          <NavLink
                            key={child.id}
                            to={child.to}
                            className={({ isActive }) =>
                              `menu-child-item ${isActive ? "active" : ""}`
                            }
                            onClick={() => setOpen(false)}
                          >
                            <Icon icon={getChildIcon(child.id)} size={16} className="menu-child-icon" />
                            <span className="menu-text">
                              <IntlMessages id={child.id} />
                            </span>
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <NavLink
                    to={menu.to}
                    className={({ isActive }) =>
                      `menu-parent ${isActive ? "active" : ""}`
                    }
                    onClick={() => setOpen(false)}
                  >
                    <div className="menu-parent-content">
                      <Icon icon={getMenuIcon(menu.id)} size={18} className="menu-icon" />
                      <span className="menu-text">
                        <IntlMessages id={menu.id} />
                      </span>
                    </div>
                  </NavLink>
                )}
              </div>
            );
          })}
        </div>

        {/* Mobile footer */}
        <div className="sidebar-footer mobile-only">
          <select
            className="language-selector"
            value={i18n.language}
            onChange={(e) => i18n.changeLanguage(e.target.value)}
          >
            <option value="en">English</option>
            <option value="de">Deutsch</option>
          </select>

          <button
            className="logout-btn danger"
            onClick={handleLogout}
            title="Logout"
          >
            <Icon icon={standby} size={18} />
            <span className="ms-2">Logout</span>
          </button>
        </div>
      </div>

      {/* Overlay for mobile */}
      {open && <div className="sidebar-overlay" onClick={() => setOpen(false)} />}
    </>
  );
};

export default connect(null, { userLogout, showModal })(Sidebar);
